if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);  // Desktop number of slides
      this.tabletItems = parseInt(this.dataset.tabletItems);  // Tablet number of slides

      this.sliderInstance = false;

      if (!this.enableSlider) return;

      this.init();

      // Listen to screen resize events for recalculating the slider setup
      window.addEventListener('resize', () => this.init());

      // Listen for mobile vs tablet changes
      document.addEventListener('matchMobile', () => this.init());
      document.addEventListener('unmatchMobile', () => this.init());
    }

    init() {
      // Check the current screen width to determine if we need the slider
      const isMobile = FoxTheme.config.mqlMobile;
      if (isMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--column-gap');
      const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

      // Dynamically calculate slidesPerView based on screen width
      const slidesPerViewDesktop = this.items > 6 ? 6 : this.items;  // Adjust for desktop
      const slidesPerViewTablet = this.tabletItems > 3 ? 3 : this.tabletItems;  // Adjust for tablets
      const slidesPerViewMobile = 1;  // Show only one slide on mobile

      // Setup swiper options with dynamic breakpoints
      const sliderOptions = {
        modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Pagination],
        slidesPerView: slidesPerViewTablet,  // Default to tablet settings
        spaceBetween: spaceBetween,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        loop: false,  // Disable loop if you don't want the slider to wrap around
        breakpoints: {
          1280: {
            slidesPerView: slidesPerViewDesktop,  // 5 slides per view on desktop
          },
          768: {
            slidesPerView: slidesPerViewTablet,   // 3 slides per view on tablet
          },
          480: {
            slidesPerView: slidesPerViewMobile,   // 1 slide per view on mobile
          },
        },
      };

      // Only initialize the slider if it's not already initialized
      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        // Add event listener for slide change to disable prev/next buttons
        this.sliderInstance.slider.on('slideChange', () => {
          this.updateNavigationButtons();
        });

        // Initial update of the navigation buttons
        this.updateNavigationButtons();
      }

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId !== this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);
          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);

      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }

    // Disable/Enable navigation buttons based on the slider position
    updateNavigationButtons() {
      const swiper = this.sliderInstance.slider;
      const prevButton = this.section.querySelector(this.selectors.prevEl);
      const nextButton = this.section.querySelector(this.selectors.nextEl);

      // Disable "prev" button if at the first slide
      if (swiper.isBeginning) {
        prevButton.setAttribute('disabled', 'true');
      } else {
        prevButton.removeAttribute('disabled');
      }

      // Disable "next" button if at the last slide
      if (swiper.isEnd) {
        nextButton.setAttribute('disabled', 'true');
      } else {
        nextButton.removeAttribute('disabled');
      }
    }
  }
  customElements.define('collection-list', CollectionList);
}
